import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type ComparisonRow = {
  label: string
  youtube: string
  facebook: string
}

const AUDIENCE_DATA: ComparisonRow[] = [
  {
    label: "UK monthly active users",
    youtube: "~50 million+",
    facebook: "~45 million",
  },
  {
    label: "Primary age demographic",
    youtube: "25-54",
    facebook: "30-55+",
  },
  {
    label: "Homeowner skew",
    youtube: "Strong (broad across all demographics)",
    facebook: "Strong (established homeowners)",
  },
  {
    label: "Peak property browsing time",
    youtube: "Evenings and weekends",
    facebook: "12-2pm, 7-9pm weekdays",
  },
  {
    label: "Content discovery model",
    youtube: "Search + recommended + subscriptions",
    facebook: "Friends + Groups + Marketplace + feed",
  },
  {
    label: "User intent",
    youtube: "Active search (high intent)",
    facebook: "Passive discovery (interruption-based)",
  },
  {
    label: "Average session duration",
    youtube: "~30 minutes",
    facebook: "~20 minutes",
  },
]

const FORMAT_DATA: ComparisonRow[] = [
  {
    label: "Primary ad format",
    youtube: "TrueView in-stream (skippable after 5s)",
    facebook: "In-feed video, Stories, Reels",
  },
  {
    label: "Short-form option",
    youtube: "Bumper ads (6s, non-skippable)",
    facebook: "Reels ads (up to 60s)",
  },
  {
    label: "Discovery placement",
    youtube: "Discovery ads (search results, related)",
    facebook: "Marketplace, Groups, right column",
  },
  {
    label: "Ideal video length",
    youtube: "15-60 seconds (in-stream), 2-5 min (organic)",
    facebook: "15-30 seconds (feed), 5-15s (Stories/Reels)",
  },
  {
    label: "Aspect ratio",
    youtube: "16:9 landscape (primary), 9:16 for Shorts",
    facebook: "1:1 or 4:5 (feed), 9:16 (Stories/Reels)",
  },
  {
    label: "Sound expectations",
    youtube: "Sound on by default",
    facebook: "Often sound off (captions essential)",
  },
  {
    label: "Call-to-action options",
    youtube: "Overlay CTAs, companion banners, end screens",
    facebook: "CTA buttons (Learn More, Book Now, etc.)",
  },
]

const COST_DATA: ComparisonRow[] = [
  {
    label: "Pricing model",
    youtube: "Cost per view (CPV) or CPM",
    facebook: "CPM (cost per 1,000 impressions)",
  },
  {
    label: "Typical cost",
    youtube: "\u00A30.02-0.06 per view",
    facebook: "\u00A33-7 CPM",
  },
  {
    label: "Minimum daily budget",
    youtube: "\u00A35-10/day recommended",
    facebook: "\u00A35/day minimum per ad set",
  },
  {
    label: "Geographic targeting",
    youtube: "Postcode, radius, region",
    facebook: "Postcode, radius, region, drop pin",
  },
  {
    label: "Demographic targeting",
    youtube: "Age, gender, household income, parental status",
    facebook: "Age, gender, life events (recently moved, engaged)",
  },
  {
    label: "Interest targeting",
    youtube: "In-market audiences (property, moving services)",
    facebook: "Interests, behaviours, life events, Lookalikes",
  },
  {
    label: "Retargeting",
    youtube: "Video viewers, website visitors, customer lists",
    facebook: "Video viewers, page engagers, website visitors, Lookalikes",
  },
  {
    label: "Billing",
    youtube: "Only pay when viewer watches 30s (or full ad if shorter)",
    facebook: "Pay per impression regardless of watch time",
  },
]

type StatCard = { value: string; label: string; detail: string }

const STATS: StatCard[] = [
  {
    value: "82%",
    label: "Video drives decisions",
    detail:
      "of UK property buyers say video influenced their decision to book a viewing, according to industry surveys.",
  },
  {
    value: "50M+",
    label: "YouTube UK reach",
    detail:
      "YouTube reaches more UK adults than any social platform, with over 50 million monthly active users.",
  },
  {
    value: "\u00A30.03",
    label: "Average YouTube CPV",
    detail:
      "YouTube\u2019s TrueView model means you only pay when someone actually watches your ad, keeping costs predictable.",
  },
  {
    value: "2.5x",
    label: "Higher recall",
    detail:
      "Video ads on YouTube deliver 2.5 times higher ad recall than static display ads, making listings more memorable.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Should estate agents use YouTube Ads or Facebook Ads?",
    a: "It depends on your goals. Facebook is stronger for hyper-local demographic targeting and reaching people going through life events (recently engaged, new job). YouTube excels at reaching high-intent audiences who are actively searching for property content. Most successful agents use both, allocating roughly 60% to Facebook for lead generation and 40% to YouTube for brand awareness and retargeting.",
  },
  {
    q: "How much should an estate agent spend on video ads per month?",
    a: "For a single-branch agency, a starting budget of \u00A3500-1,000 per month across both platforms is a reasonable test. Allocate roughly \u00A3300-600 to Facebook for local lead generation and \u00A3200-400 to YouTube for broader awareness. Multi-branch agencies typically spend \u00A31,500-5,000 per month. The key is to start small, measure results, and scale what works.",
  },
  {
    q: "What video format works best for property ads on YouTube?",
    a: "For YouTube in-stream ads, 15 to 30-second property highlight videos work best. Lead with the most impressive room or feature in the first 5 seconds (before the skip button appears). For organic YouTube content, longer 2 to 5-minute property tours perform well. YouTube Shorts (vertical, under 60 seconds) are growing rapidly and are ideal for quick listing teasers.",
  },
  {
    q: "Do I need different video content for YouTube and Facebook?",
    a: "Ideally, yes. YouTube viewers expect landscape (16:9) video with sound on, while Facebook and Instagram favour square (1:1) or vertical (9:16) video optimised for sound-off viewing with captions. QuiteRight generates video in multiple formats from a single listing, so you can create platform-specific content without additional editing work.",
  },
  {
    q: "How do I measure ROI on property video ads?",
    a: "Track three key metrics: cost per lead (form fills, calls, or messages from the ad), cost per viewing booked, and time on market for advertised properties versus non-advertised ones. On YouTube, monitor view rate and cost per view. On Facebook, track cost per result and relevance score. Set up conversion tracking on both platforms to attribute website actions back to your ads.",
  },
  {
    q: "Can I target specific postcodes with YouTube and Facebook Ads?",
    a: "Yes, both platforms support geographic targeting by postcode, town, city, and radius. Facebook allows targeting down to a 1-mile radius, making it excellent for hyper-local campaigns. YouTube (via Google Ads) supports postcode and radius targeting as well, though the minimum radius is slightly larger. Both platforms also let you exclude areas to avoid wasting budget.",
  },
  {
    q: "What is YouTube TrueView and why does it matter for estate agents?",
    a: "TrueView is YouTube\u2019s skippable ad format where you only pay when a viewer watches at least 30 seconds of your ad (or the full ad if it is shorter than 30 seconds). This is significant for estate agents because it means you only pay for engaged viewers who are genuinely interested in your property content, not people who skip past immediately.",
  },
  {
    q: "How does QuiteRight help with video ads for both platforms?",
    a: "QuiteRight creates professional property video ads from your listing photos in about 60 seconds. Each video is generated in multiple aspect ratios (16:9 for YouTube, 1:1 and 9:16 for Facebook and Instagram), with branded overlays and captions. This means you can launch campaigns on both platforms from a single listing without hiring a videographer or editor.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function PlatformBadge({
  name,
  colour,
}: {
  name: string
  colour: string
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white"
      style={{ backgroundColor: colour }}
    >
      {name}
    </span>
  )
}

function ComparisonTable({
  rows,
  title,
}: {
  rows: ComparisonRow[]
  title: string
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              {title}
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <PlatformBadge name="YouTube" colour="#FF0000" />
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <PlatformBadge name="Facebook" colour="#1877F2" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${i % 2 === 0 ? "bg-cream/30" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.label}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.youtube}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm text-slate">
                {row.facebook}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function YoutubeAdsVsFacebookAds({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "YouTube Ads vs Facebook Ads for Estate Agents: Which Platform Delivers Better Results?",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-03-12",
            dateModified: "2026-03-12",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "YouTube Ads vs Facebook Ads for Estate Agents" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Video advertising comparison 2026
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            YouTube Ads vs Facebook Ads for estate agents
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Two platforms, two approaches to video advertising. This guide
            compares <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer">YouTube</a> and <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer">Facebook</a> for UK estate agents deciding where
            to invest their video ad budget for maximum return.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <PlatformBadge name="YouTube" colour="#FF0000" />
            <PlatformBadge name="Facebook" colour="#1877F2" />
          </div>
        </div>
      </section>

      <SocialShareBar />

      {/* Stats */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The opportunity
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Why video advertising matters for agents
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map((stat, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6 text-center">
                  <p className="font-display text-4xl font-bold text-charcoal">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    {stat.label}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate">
                    {stat.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Platform deep dives */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Platform profiles
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Each platform at a glance
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {/* YouTube */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <PlatformBadge name="YouTube" colour="#FF0000" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The intent-driven video platform
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  YouTube, owned by <a href="https://www.google.com" target="_blank" rel="noopener noreferrer">Google</a>, is where people go to watch video on purpose. With
                  over 50 million UK users, it is the largest video platform
                  in the country. Viewers actively search for content,
                  including property tours, area guides, and moving advice.
                  This intent-driven behaviour means your ads reach people who
                  are already in a video-watching mindset, leading to higher
                  engagement and recall.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Best for:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Brand awareness and property showcases
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Reaching high-intent audiences via search
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Retargeting website visitors with video
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Cost-effective CPV (pay only for views)
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Requires quality video content to compete
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Less granular life-event targeting than Facebook
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Steeper learning curve via <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer">Google Ads</a> interface
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* Facebook */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <PlatformBadge name="Facebook" colour="#1877F2" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  The hyper-local lead engine
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Facebook remains the most powerful platform for local
                  property advertising, managed through <a href="https://www.facebook.com/business/ads" target="_blank" rel="noopener noreferrer">Meta Ads Manager</a>. With 45 million UK users and
                  unmatched demographic targeting, it excels at putting your
                  listings in front of people likely to move. Life-event
                  targeting (recently engaged, new job, expecting a child)
                  lets you reach potential movers at exactly the right moment.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Best for:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Hyper-local targeting by postcode and radius
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Life-event targeting for likely movers
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Lead generation forms (in-app, no landing page needed)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Lookalike audiences from your existing client list
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Videos often play with sound off
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Organic reach for business pages is very low
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Younger demographics increasingly inactive
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Comparison table: Audience & Reach */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Head to head
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Audience and reach
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-sm leading-relaxed text-slate">
              Both platforms offer massive UK audiences, but they attract
              different types of viewers. YouTube skews towards active
              searchers, while Facebook reaches passive browsers during their
              daily scroll. For a broader comparison across social platforms, see our <Link href="/guides/instagram-vs-facebook-vs-tiktok-property-ads">Instagram vs Facebook vs TikTok guide</Link>.
            </p>
          </ScrollReveal>

          <ScrollReveal>
            <div className="mt-12 rounded-xl border border-stone bg-white p-6 md:p-8">
              <ComparisonTable rows={AUDIENCE_DATA} title="Audience &amp; Reach" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Comparison table: Ad Formats & Creative */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Creative comparison
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Ad formats and creative
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-sm leading-relaxed text-slate">
              YouTube is built for video. Every ad is a video ad. Facebook
              offers more variety in formats but video consistently
              outperforms static content in the property sector. Learn more in our <Link href="/guides/property-video-marketing">property video marketing guide</Link>.
            </p>
          </ScrollReveal>

          <ScrollReveal>
            <div className="mt-12 rounded-xl border border-stone bg-white p-6 md:p-8">
              <ComparisonTable rows={FORMAT_DATA} title="Formats &amp; Creative" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Comparison table: Targeting & Cost */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Budget and targeting
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Targeting and cost
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-sm leading-relaxed text-slate">
              YouTube charges per view (you only pay for engaged viewers),
              while Facebook charges per impression. Both offer strong
              geographic and demographic targeting for property campaigns. For a detailed breakdown, see our <Link href="/guides/cost-per-lead-video-ads-vs-traditional-property-marketing">cost per lead guide</Link>.
            </p>
          </ScrollReveal>

          <ScrollReveal>
            <div className="mt-12 rounded-xl border border-stone bg-white p-6 md:p-8">
              <ComparisonTable rows={COST_DATA} title="Targeting &amp; Cost" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Strategy recommendations */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Strategy by agency type
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Which platform suits your agency?
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-red">
                  Single-branch independents
                </p>
                <h3 className="mt-3 font-display text-lg font-bold text-charcoal">
                  Start with Facebook
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  For smaller agencies with limited budgets, Facebook offers
                  the most direct path to leads. Its hyper-local targeting
                  means every pound targets people in your patch. Our <Link href="/guides/social-media-estate-agents">social media for estate agents</Link> guide covers organic strategy alongside paid. Use video
                  ads in the feed to showcase new listings and run lead
                  generation campaigns for vendor acquisition.
                </p>
                <div className="mt-4 rounded-lg bg-cream/60 p-4">
                  <p className="text-xs font-semibold text-charcoal">
                    Recommended split
                  </p>
                  <p className="mt-1 text-sm text-slate">
                    70% Facebook, 30% YouTube
                  </p>
                  <p className="mt-2 text-xs font-semibold text-charcoal">
                    Monthly budget guide
                  </p>
                  <p className="mt-1 text-sm text-slate">
                    {"\u00A3"}500-1,000 total
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border-2 border-red/20 bg-red/5 p-6">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-red">
                    Multi-branch corporates
                  </p>
                  <span className="rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red">
                    Best ROI
                  </span>
                </div>
                <h3 className="mt-3 font-display text-lg font-bold text-charcoal">
                  Use both platforms
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Larger agencies benefit most from a dual-platform strategy.
                  Use Facebook for bottom-of-funnel lead generation (valuation
                  requests, viewing bookings) and YouTube for top-of-funnel
                  brand awareness. <Link href="/guides/retargeting-vs-cold-audience-property-video-ads">Retarget</Link> YouTube viewers on Facebook, and
                  retarget Facebook engagers on YouTube for maximum recall.
                </p>
                <div className="mt-4 rounded-lg bg-white/60 p-4">
                  <p className="text-xs font-semibold text-charcoal">
                    Recommended split
                  </p>
                  <p className="mt-1 text-sm text-slate">
                    55% Facebook, 45% YouTube
                  </p>
                  <p className="mt-2 text-xs font-semibold text-charcoal">
                    Monthly budget guide
                  </p>
                  <p className="mt-1 text-sm text-slate">
                    {"\u00A3"}2,000-5,000 total
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-red">
                  Premium and rural agents
                </p>
                <h3 className="mt-3 font-display text-lg font-bold text-charcoal">
                  Lean into YouTube
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  If you sell higher-value properties where buyers research
                  extensively before viewing, YouTube is your strongest
                  channel. Longer property tour videos perform exceptionally
                  well for premium homes. Buyers searching for specific areas
                  or property types will find your content through YouTube
                  search and recommendations.
                </p>
                <div className="mt-4 rounded-lg bg-cream/60 p-4">
                  <p className="text-xs font-semibold text-charcoal">
                    Recommended split
                  </p>
                  <p className="mt-1 text-sm text-slate">
                    40% Facebook, 60% YouTube
                  </p>
                  <p className="mt-2 text-xs font-semibold text-charcoal">
                    Monthly budget guide
                  </p>
                  <p className="mt-1 text-sm text-slate">
                    {"\u00A3"}1,000-3,000 total
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* QuiteRight integration */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="rounded-2xl border border-stone bg-white p-8 md:p-12">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Create video for both platforms
              </p>
              <h2 className="mt-4 font-display text-3xl font-bold text-charcoal md:text-4xl">
                Professional property videos in 60 seconds
                <span className="text-red">.</span>
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate">
                The biggest barrier to running video ads on YouTube and
                Facebook is creating the video itself. QuiteRight removes
                that barrier entirely. Paste a listing URL, and we generate
                branded property videos in every format you need: 16:9 for
                YouTube, 1:1 for Facebook feed, and 9:16 for Stories and
                Reels. See <Link href="/how-it-works">how it works</Link>.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg bg-cream/60 p-4 text-center">
                  <p className="font-display text-2xl font-bold text-charcoal">
                    60s
                  </p>
                  <p className="mt-1 text-xs text-slate">
                    From listing to video
                  </p>
                </div>
                <div className="rounded-lg bg-cream/60 p-4 text-center">
                  <p className="font-display text-2xl font-bold text-charcoal">
                    3 formats
                  </p>
                  <p className="mt-1 text-xs text-slate">
                    YouTube, feed, and Stories
                  </p>
                </div>
                <div className="rounded-lg bg-cream/60 p-4 text-center">
                  <p className="font-display text-2xl font-bold text-charcoal">
                    Branded
                  </p>
                  <p className="mt-1 text-xs text-slate">
                    Your logo, colours, and contact details
                  </p>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  href="/signup"
                  className="inline-flex items-center gap-2 rounded-lg bg-red px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-red/90"
                >
                  Try QuiteRight free
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    className="size-3.5"
                  >
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Common questions
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Frequently asked questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal>
            <div className="mt-12">
              <FaqAccordion items={FAQ} />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Related guides */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Keep reading
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Related guides
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <ScrollReveal>
              <Link
                href="/guides/instagram-vs-facebook-vs-tiktok-property-ads"
                className="group block rounded-xl border border-stone bg-white p-6 transition-shadow hover:shadow-md"
              >
                <p className="text-xs font-medium uppercase tracking-wider text-red">
                  Social comparison
                </p>
                <h3 className="mt-2 font-display text-base font-bold text-charcoal group-hover:text-red">
                  Instagram vs Facebook vs TikTok for Property Ads
                </h3>
                <p className="mt-2 text-sm text-slate">
                  Compare all three social platforms for organic and paid
                  property advertising.
                </p>
              </Link>
            </ScrollReveal>

            <ScrollReveal>
              <Link
                href="/guides/property-video-marketing"
                className="group block rounded-xl border border-stone bg-white p-6 transition-shadow hover:shadow-md"
              >
                <p className="text-xs font-medium uppercase tracking-wider text-red">
                  Video marketing
                </p>
                <h3 className="mt-2 font-display text-base font-bold text-charcoal group-hover:text-red">
                  Property Video Marketing Guide
                </h3>
                <p className="mt-2 text-sm text-slate">
                  Everything you need to know about using video to market
                  property listings in 2026.
                </p>
              </Link>
            </ScrollReveal>

            <ScrollReveal>
              <Link
                href="/guides/social-media-estate-agents"
                className="group block rounded-xl border border-stone bg-white p-6 transition-shadow hover:shadow-md"
              >
                <p className="text-xs font-medium uppercase tracking-wider text-red">
                  Social media
                </p>
                <h3 className="mt-2 font-display text-base font-bold text-charcoal group-hover:text-red">
                  Social Media for Estate Agents
                </h3>
                <p className="mt-2 text-sm text-slate">
                  The complete guide to building your agency presence across
                  every major social platform.
                </p>
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Ready to start?
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl">
              Create video ads for YouTube and Facebook today
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/70">
              Stop choosing between platforms. With QuiteRight, you get
              professional property videos in every format, ready to upload
              to YouTube, Facebook, Instagram, and TikTok. <Link href="/sign-up">Get started</Link> and create your first video in under a minute.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                href="/signup"
                className="inline-flex items-center gap-2 rounded-lg bg-red px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-red/90"
              >
                Get started free
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className="size-3.5"
                >
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              </Link>
              <Link
                href="/guides"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/40"
              >
                Browse all guides
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
